import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";

export default function Catalog() {
    const [searchParams, setSearchParams] = useSearchParams();
    const category = searchParams.get("category") || "all";
    const q = searchParams.get("q") || "";
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t } = useI18n();

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (category !== "all") params.category = category;
        if (q) params.q = q;
        api.get("/products", { params })
            .then((r) => setProducts(r.data))
            .finally(() => setLoading(false));
    }, [category, q]);

    const setCategory = (c) => {
        const next = new URLSearchParams(searchParams);
        if (c === "all") next.delete("category");
        else next.set("category", c);
        setSearchParams(next);
    };

    const title = q
        ? t("catalog.title_search")
        : category === "tshirt"
        ? t("catalog.title_tshirt")
        : category === "cap"
        ? t("catalog.title_cap")
        : t("catalog.title_all");

    return (
        <div data-testid="catalog-page" className="mx-auto max-w-[1400px] px-5 sm:px-8 py-12 sm:py-16">
            <Seo title={title} />

            <div className="mb-10 sm:mb-14">
                <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                    {t("catalog.eyebrow")} · {new Date().getFullYear()}
                </p>
                <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tighter">
                    {title}
                </h1>
                {q ? (
                    <p className="text-sm text-white/50 mt-3" data-testid="search-results-label">
                        {t("catalog.results_for")}: <span className="text-white">« {q} »</span> · {products.length}
                    </p>
                ) : (
                    <p className="text-sm text-white/50 mt-3">
                        {products.length} {t("catalog.pieces_available")}
                    </p>
                )}
            </div>

            {!q && (
                <div className="flex gap-2 mb-10 border-b border-white/10 pb-1 overflow-x-auto">
                    {[
                        { key: "all", label: t("catalog.filter_all") },
                        { key: "tshirt", label: t("catalog.filter_tshirt") },
                        { key: "cap", label: t("catalog.filter_cap") },
                    ].map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setCategory(f.key)}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold whitespace-nowrap transition-colors ${
                                category === f.key
                                    ? "bg-white text-black"
                                    : "text-white/60 hover:text-white"
                            }`}
                            data-testid={`filter-${f.key}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="aspect-[4/5] bg-white/5 animate-pulse" />
                    ))}
                </div>
            ) : products.length === 0 ? (
                <p className="text-white/50 py-20 text-center" data-testid="catalog-empty">
                    {q ? `${t("catalog.no_search_results")} « ${q} »` : t("catalog.no_products")}
                </p>
            ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {products.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            )}
        </div>
    );
}
